import { useMemo, useRef, useState } from "react";
import { Download } from "lucide-react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DashboardBrandSeries, MetricBucket } from "../../../types";
import { chartFileName, exportChartPng, type ChartLegendEntry } from "@/lib/chartExport";
import { cn } from "@/lib/utils";

/**
 * Categorical series palette — validated with the dataviz skill's
 * `validate_palette.js` against the white card surface:
 *
 *   adjacent pairlist (lines):  CVD ΔE 15.3 (deutan) · tritan 17.1 · normal 20.8
 *   first six slots, all pairs: normal 15.6 · CVD 6.1 (warn band → relief shipped)
 *
 * Slot 0 is the product's brand indigo and is permanently the primary/hero
 * series. Ordering is the CVD-safety mechanism, not cosmetics — re-order only by
 * re-running the validator and picking among the passing orders. Never cycle: a
 * tenth series folds into the leaderboard/SOV lists instead of getting a hue.
 *
 * The sub-3:1 slots (aqua, pink, yellow) carry the documented relief: visible
 * legend chips, end-of-line labels, a crosshair tooltip, and the data table.
 */
export const SERIES_COLORS = [
  "#4f46e5", // 0 indigo — brand / primary (hero)
  "#1baf7a", // 1 aqua
  "#86198f", // 2 purple
  "#e87ba4", // 3 pink
  "#008300", // 4 green
  "#eda100", // 5 yellow
  "#e34948", // 6 red
  "#0891b2", // 7 cyan
  "#eb6834", // 8 orange
];

/** Neutral gray for the median reference line — deliberately not a series hue. */
export const MEDIAN_COLOR = "#64748b";
export const MEDIAN_KEY = "__median";

/** Hard cap: the hero plus eight competitors. Never generate a tenth hue. */
const MAX_SERIES = SERIES_COLORS.length;
/** Competitors visible before the reader turns more on from the legend. */
const DEFAULT_VISIBLE_COMPETITORS = 4;
/**
 * Every visible line carries an end-of-line name label — the chart must be
 * self-identifying in screenshots, not only on hover. The collision passes
 * below keep up to the full series cap legible (9 × 13px inside the plot).
 */
const MAX_END_LABELS = MAX_SERIES;
/** An end label must fit the reserved gutter — the legend carries the full name. */
const MAX_END_LABEL_CHARS = 18;
const END_LABEL_LINE_HEIGHT = 13;

const CHART_HEIGHT = 280;
const MARGIN_TOP = 12;
const MARGIN_BOTTOM = 24;
const AXIS_HEIGHT = 22; // XAxis band inside the container

/* --------------------------------- scales ---------------------------------- */

/** Bucket key (`YYYY-MM-DD` day/week start, `YYYY-MM` month) → epoch ms. */
function bucketToTime(bucket: string, kind: MetricBucket): number {
  if (kind === "month") {
    const [year, month] = bucket.split("-");
    return new Date(Number(year), Number(month) - 1, 1).getTime();
  }
  return new Date(bucket + "T00:00:00").getTime();
}

/** Axis/tooltip label per granularity: "Jul 21" (day/week) · "Jul 2026" (month). */
function formatTime(time: number, kind: MetricBucket): string {
  const date = new Date(time);
  return kind === "month"
    ? date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
    : date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export interface SeriesMeta {
  key: string;
  brandId: number;
  name: string;
  color: string;
  isPrimary: boolean;
  latest: number | null;
}

/* ------------------------------ series mapping ------------------------------ */

/**
 * Series cap + hue assignment. Relevance = `latest` (latest sentiment for field
 * charts, window share for SOV) picks which eight competitors make the plot; hues
 * then follow the incoming order so they never track rank. Exported so a caller
 * rendering its own legend (an external standings list) colors it identically.
 */
export function assignTrendSeries(
  brands: DashboardBrandSeries[],
  heroBrandId: number | null,
  colorOverrides?: Record<number, string>,
): SeriesMeta[] {
  const withData = brands.filter((b) => b.series.length > 0);
  const primary = withData.find((b) => b.brand_id === heroBrandId)
    ?? withData.find((b) => b.is_primary)
    ?? null;
  const competitors = withData.filter((b) => b.brand_id !== primary?.brand_id);
  const slots = primary ? MAX_SERIES - 1 : MAX_SERIES;
  const keptIds = new Set(
    [...competitors]
      .sort((a, b) => (b.latest ?? -Infinity) - (a.latest ?? -Infinity))
      .slice(0, slots)
      .map((b) => b.brand_id),
  );
  const kept = competitors.filter((b) => keptIds.has(b.brand_id));
  const ordered = primary ? [primary, ...kept] : kept;
  return ordered.map((b, i) => ({
    key: `s${b.brand_id}`,
    brandId: b.brand_id,
    name: b.brand_name,
    color: b.brand_id === primary?.brand_id
      ? SERIES_COLORS[0]!
      : colorOverrides?.[b.brand_id] ?? SERIES_COLORS[i % SERIES_COLORS.length]!,
    isPrimary: b.brand_id === primary?.brand_id,
    latest: b.latest,
  }));
}

/** Default hidden set: everything past the primary + top competitors by relevance. */
export function defaultHiddenSeries(series: SeriesMeta[]): Set<string> {
  const byRelevance = [...series]
    .filter((s) => !s.isPrimary)
    .sort((a, b) => (b.latest ?? -Infinity) - (a.latest ?? -Infinity));
  return new Set(byRelevance.slice(DEFAULT_VISIBLE_COMPETITORS).map((s) => s.key));
}

/**
 * Legend selection state for one series set: the hidden keys and a toggle.
 * Derived rather than seeded through an effect, so the first paint is already the
 * capped view (no flash of all nine lines) and the reader's toggles reset by
 * themselves whenever the series set changes. Exported for `legend="external"`
 * callers, who own the selection the chart would otherwise keep to itself.
 */
export function useTrendSeriesToggles(series: SeriesMeta[], medianLabel?: string) {
  // The reader's legend choices, tagged with the series set they were made
  // against — a stale tag falls back to the default visible set.
  const [toggled, setToggled] = useState<{ sig: string; hidden: Set<string> } | null>(null);
  const signature = series.map((s) => s.key).join("|") + `#${medianLabel ?? ""}`;
  const defaultHidden = useMemo(() => defaultHiddenSeries(series), [series]);
  return {
    hidden: toggled?.sig === signature ? toggled.hidden : defaultHidden,
    // Functional update: two toggles inside one batch must both land.
    toggle: (key: string) => setToggled((prev) => {
      const next = new Set(prev?.sig === signature ? prev.hidden : defaultHidden);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { sig: signature, hidden: next };
    }),
  };
}

/* --------------------------------- props ----------------------------------- */

interface TrendChartProps {
  brands: DashboardBrandSeries[];
  bucket: MetricBucket;
  heroBrandId: number | null;
  /** Explicit brand_id → hex color; wins over the palette (hero stays indigo). */
  colorOverrides?: Record<number, string>;
  /** Optional reference line (e.g. a field median), positionally aligned to the sorted bucket union. */
  medianSeries?: { label: string; points: (number | null)[] };
  /** Appended to values in the legend, tooltip, table and end labels (e.g. '%'). */
  valueSuffix?: string;
  /** Let the y-axis scale to the data's own max instead of clamping the ceiling to 100. */
  autoScale?: boolean;
  /** Card title drawn onto the exported PNG. */
  title?: string;
  /** Second line on the exported PNG — timeframe / lens caption. */
  caption?: string;
  /** Filename stem: `{topic}-{chart}-{date}.png`. */
  exportTopic?: string;
  exportChart?: string;
  /**
   * 'chips' (default) draws the chart's own legend chips. 'external' draws none —
   * the caller renders the legend (e.g. a standings list) and owns the selection
   * through `hiddenKeys`/`onToggleSeries`, both built off `assignTrendSeries`.
   */
  legend?: "chips" | "external";
  hiddenKeys?: Set<string>;
  onToggleSeries?: (key: string) => void;
  /** Suppress the "full list below" overflow note when the full list sits beside the chart. */
  hideOverflowNote?: boolean;
}

/**
 * Multi-brand trend lines on a real time axis (gaps in time render as gaps),
 * with a crosshair tooltip listing every visible series, an interactive legend
 * that toggles series without ever repainting the survivors, end-of-line direct
 * labels while the field is small, a PNG export and a data table.
 *
 * Color follows the entity: hues are assigned once from the incoming series
 * order (primary always slot 0), so hiding a competitor never recolors another.
 */
export function TrendChart({
  brands,
  bucket,
  heroBrandId,
  colorOverrides,
  medianSeries,
  valueSuffix = "",
  autoScale = false,
  title,
  caption,
  exportTopic,
  exportChart,
  legend = "chips",
  hiddenKeys,
  onToggleSeries,
  hideOverflowNote = false,
}: TrendChartProps) {
  const [showTable, setShowTable] = useState(false);
  const [exportError, setExportError] = useState("");
  const plotRef = useRef<HTMLDivElement>(null);

  const withData = useMemo(() => brands.filter((b) => b.series.length > 0), [brands]);

  // Bucket union across EVERY passed series (not just the plotted cap) so the
  // positional median alignment callers computed stays valid.
  const allBuckets = useMemo(
    () => Array.from(new Set(withData.flatMap((b) => b.series.map((p) => p.bucket)))).sort(),
    [withData],
  );

  const series = useMemo(
    () => assignTrendSeries(withData, heroBrandId, colorOverrides),
    [withData, heroBrandId, colorOverrides],
  );
  const overflowCount = withData.length - series.length;

  // Selection: the chart's own unless an external legend drives it.
  const internal = useTrendSeriesToggles(series, medianSeries?.label);
  const hidden = legend === "external" && hiddenKeys ? hiddenKeys : internal.hidden;
  const toggle = legend === "external" && onToggleSeries ? onToggleSeries : internal.toggle;

  const visible = series.filter((s) => !hidden.has(s.key));
  const medianVisible = !!medianSeries && !hidden.has(MEDIAN_KEY);

  // One row per bucket on the time axis; nulls break the line at real gaps.
  const rows = useMemo(() => {
    const byBrand = new Map(withData.map((b) => [b.brand_id, new Map(b.series.map((p) => [p.bucket, p.avg]))]));
    return allBuckets.map((b, i) => {
      const row: Record<string, number | null> = { t: bucketToTime(b, bucket) };
      for (const s of series) row[s.key] = byBrand.get(s.brandId)?.get(b) ?? null;
      if (medianSeries) row[MEDIAN_KEY] = medianSeries.points[i] ?? null;
      return row;
    });
  }, [allBuckets, series, withData, medianSeries, bucket]);

  if (allBuckets.length === 0) {
    return <p className="py-12 text-center text-[13px] text-slate-400">No data points in this period yet.</p>;
  }

  // Y domain follows what is actually plotted: a series the reader turned off
  // must not stretch the axis and squash the lines they asked to see. Clamped to
  // the metric's 0–100 domain unless `autoScale` follows the data's own max.
  const values = [
    ...visible.flatMap((s) => rows.map((r) => r[s.key]).filter((v): v is number => v !== null && v !== undefined)),
    ...(medianVisible ? rows.map((r) => r[MEDIAN_KEY]).filter((v): v is number => v !== null && v !== undefined) : []),
  ];
  const floor = values.length > 0 ? Math.max(0, Math.floor(Math.min(...values)) - 8) : 0;
  const rawCeil = values.length > 0 ? Math.ceil(Math.max(...values)) + 8 : 100;
  const ceil = autoScale ? rawCeil : Math.min(100, rawCeil);
  const span = Math.max(ceil - floor, 1);
  const yTicks = [floor, Math.round((floor + ceil) / 2), ceil];

  // End-of-line direct labels — only while ≤4 series are visible, else the
  // legend carries identity. Nudges are precomputed against the same linear
  // scale Recharts uses, then applied as a dy so labels never overlap.
  const showEndLabels = visible.length > 0 && visible.length <= MAX_END_LABELS;
  const plotHeight = CHART_HEIGHT - MARGIN_TOP - MARGIN_BOTTOM - AXIS_HEIGHT;
  const yOf = (v: number) => MARGIN_TOP + ((ceil - v) / span) * plotHeight;
  const endPoints = new Map<string, { index: number; dy: number }>();
  if (showEndLabels) {
    const raw = visible
      .map((s) => {
        for (let i = rows.length - 1; i >= 0; i--) {
          const v = rows[i]![s.key];
          if (v !== null && v !== undefined) return { key: s.key, index: i, y: yOf(v) };
        }
        return null;
      })
      .filter((e): e is { key: string; index: number; y: number } => e !== null)
      .sort((a, b) => a.y - b.y);
    // Two passes: push down off collisions, then push back up off the axis band,
    // so a label never lands on the x-axis ticks or outside the plot.
    const minY = MARGIN_TOP + 5;
    const maxY = MARGIN_TOP + plotHeight - 3;
    const placed: number[] = [];
    let lastY = -Infinity;
    for (const e of raw) placed.push((lastY = Math.max(e.y, lastY + END_LABEL_LINE_HEIGHT, minY)));
    let nextY = Infinity;
    for (let i = placed.length - 1; i >= 0; i--) {
      placed[i] = nextY = Math.min(placed[i]!, nextY - END_LABEL_LINE_HEIGHT, maxY);
    }
    raw.forEach((e, i) => endPoints.set(e.key, { index: e.index, dy: placed[i]! - e.y }));
  }
  // A bucket whose neighbours are both empty has no line segment to live on, so
  // it must always carry a dot or it renders as nothing at all.
  const dotsAlways = allBuckets.length <= 14;
  const isolated = new Map<string, Set<number>>();
  for (const s of series) {
    const set = new Set<number>();
    for (let i = 0; i < rows.length; i++) {
      const v = rows[i]![s.key];
      if (v === null || v === undefined) continue;
      const prev = i > 0 ? rows[i - 1]![s.key] : null;
      const next = i < rows.length - 1 ? rows[i + 1]![s.key] : null;
      if ((prev === null || prev === undefined) && (next === null || next === undefined)) set.add(i);
    }
    isolated.set(s.key, set);
  }

  // Labels are truncated to the gutter they get, never clipped by it.
  const endLabel = (name: string) =>
    name.length > MAX_END_LABEL_CHARS ? `${name.slice(0, MAX_END_LABEL_CHARS - 1).trimEnd()}…` : name;
  const longestEndLabel = showEndLabels
    ? visible.reduce((m, s) => Math.max(m, endLabel(s.name).length), 0)
    : 0;
  const rightMargin = showEndLabels ? Math.round(16 + longestEndLabel * 6.1) : 12;

  // A legend is mandatory for ≥2 series and pointless (and a footgun — it could
  // hide the only line) for exactly one.
  const showLegend = legend === "chips" && (series.length > 1 || !!medianSeries);

  const downloadPng = async () => {
    const svg = plotRef.current?.querySelector("svg");
    if (!svg) return;
    setExportError("");
    try {
      // The HTML legend lives outside the SVG, so the export needs the visible
      // series handed to it — in on-screen order, median last.
      const legend: ChartLegendEntry[] = [
        ...visible.map((s) => ({
          label: s.name,
          color: s.color,
          value: s.latest === null ? undefined : fmt(s.latest),
        })),
        ...(medianVisible ? [{ label: medianSeries!.label, color: MEDIAN_COLOR, dashed: true }] : []),
      ];
      await exportChartPng(svg as SVGSVGElement, {
        title: title ?? "Trend",
        caption,
        fileName: chartFileName(exportTopic ?? title ?? "prism", exportChart ?? "trend"),
        legend,
      });
    } catch (e: any) {
      setExportError(e?.message || "Could not export the chart.");
    }
  };

  const fmt = (v: number, digits = 1) => `${v.toFixed(digits)}${valueSuffix}`;

  return (
    <div>
      <div ref={plotRef} style={{ width: "100%", height: CHART_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ top: MARGIN_TOP, right: rightMargin, bottom: MARGIN_BOTTOM, left: 0 }}>
            <defs>
              <linearGradient id="trend-hero-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={SERIES_COLORS[0]} stopOpacity="0.12" />
                <stop offset="100%" stopColor={SERIES_COLORS[0]} stopOpacity="0" />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} stroke="#f1f5f9" strokeWidth={1} />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={(t: number) => formatTime(t, bucket)}
              tick={{ fill: "#94a3b8", fontSize: 10 }}
              tickMargin={8}
              height={AXIS_HEIGHT}
              minTickGap={28}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
            />
            <YAxis
              domain={[floor, ceil]}
              ticks={yTicks}
              tick={{ fill: "#94a3b8", fontSize: 10 }}
              width={38}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              isAnimationActive={false}
              cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }}
              content={(props: any) => (
                <TrendTooltip
                  active={props.active}
                  label={props.label}
                  payload={props.payload}
                  bucket={bucket}
                  valueSuffix={valueSuffix}
                  seriesByKey={new Map(series.map((s) => [s.key, s]))}
                  medianLabel={medianSeries?.label}
                />
              )}
            />

            {/* Hero area wash — a 12% tint, never a saturated block. */}
            {visible.some((s) => s.isPrimary) && (
              <Area
                type="linear"
                dataKey={visible.find((s) => s.isPrimary)!.key}
                stroke="none"
                fill="url(#trend-hero-area)"
                isAnimationActive={false}
                connectNulls={false}
                activeDot={false}
                legendType="none"
                tooltipType="none"
              />
            )}

            {medianVisible && (
              <Line
                type="linear"
                dataKey={MEDIAN_KEY}
                name={medianSeries!.label}
                stroke={MEDIAN_COLOR}
                strokeWidth={1.5}
                strokeDasharray="5 4"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: "#ffffff" }}
                isAnimationActive={false}
                connectNulls={false}
              />
            )}

            {visible.map((s) => (
              <Line
                key={s.key}
                type="linear"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={s.isPrimary ? 2.5 : 2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={dotsAlways
                  ? { r: 4, fill: s.color, stroke: "#ffffff", strokeWidth: 2 }
                  : (props: any) => (
                    isolated.get(s.key)?.has(props.index) && Number.isFinite(props.cy)
                      ? <circle key={`${s.key}-dot-${props.index}`} cx={props.cx} cy={props.cy} r={4} fill={s.color} stroke="#ffffff" strokeWidth={2} />
                      : <g key={`${s.key}-dot-${props.index}`} />
                  )}
                activeDot={{ r: 4.5, fill: s.color, stroke: "#ffffff", strokeWidth: 2 }}
                isAnimationActive={false}
                connectNulls={false}
                label={showEndLabels && endPoints.has(s.key)
                  ? (props: any) => {
                    const end = endPoints.get(s.key)!;
                    if (props.index !== end.index || !Number.isFinite(props.y)) return <g key={`${s.key}-${props.index}`} />;
                    return (
                      <text
                        key={`${s.key}-label`}
                        x={props.x + 8}
                        y={props.y + end.dy + 3.5}
                        className="fill-slate-600 text-[10.5px] font-medium"
                      >
                        {endLabel(s.name)}
                      </text>
                    );
                  }
                  : false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Interactive legend — chips toggle a series in the plot, tooltip and table.
          A lone series needs no legend: the card title already names what's plotted.
          `legend="external"` draws none at all; the caller's list is the legend. */}
      {legend === "chips" && (
        <div className={cn("mt-3 flex flex-wrap items-center gap-1.5", !showLegend && "hidden")}>
          {series.map((s) => (
            <LegendChip
              key={s.key}
              color={s.color}
              name={s.name}
              value={s.latest === null ? null : `${s.latest.toFixed(0)}${valueSuffix}`}
              on={!hidden.has(s.key)}
              bold={s.isPrimary}
              onClick={() => toggle(s.key)}
            />
          ))}
          {medianSeries && (
            <LegendChip
              color={MEDIAN_COLOR}
              dashed
              name={medianSeries.label}
              value={null}
              on={medianVisible}
              onClick={() => toggle(MEDIAN_KEY)}
            />
          )}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {overflowCount > 0 && !hideOverflowNote && (
          <span className="text-[11px] text-slate-400">
            Top {series.length - 1} of {series.length - 1 + overflowCount} competitors — full list below
          </span>
        )}
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          aria-expanded={showTable}
          className="ml-auto text-[11.5px] font-medium text-slate-500 hover:text-brand-600"
        >
          {showTable ? "Hide data" : "View data"}
        </button>
        <button
          type="button"
          onClick={downloadPng}
          className="inline-flex items-center gap-1 text-[11.5px] font-medium text-slate-500 hover:text-brand-600"
        >
          <Download className="w-3.5 h-3.5" aria-hidden="true" />
          PNG
        </button>
      </div>
      {exportError && <p className="mt-1 text-[11.5px] text-red-600">{exportError}</p>}

      {showTable && (
        <div className="mt-2 overflow-x-auto rounded-md border border-slate-200">
          <table className="w-full border-collapse text-[11.5px] tabular-nums">
            <caption className="sr-only">{title ?? "Trend"} — values per period</caption>
            <thead>
              <tr className="bg-slate-50 text-left text-slate-500">
                <th scope="col" className="px-2.5 py-1.5 font-semibold">Period</th>
                {visible.map((s) => (
                  <th key={s.key} scope="col" className="px-2.5 py-1.5 text-right font-semibold whitespace-nowrap">{s.name}</th>
                ))}
                {medianVisible && (
                  <th scope="col" className="px-2.5 py-1.5 text-right font-semibold whitespace-nowrap">{medianSeries!.label}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.t} className="border-t border-slate-100">
                  <th scope="row" className="px-2.5 py-1 text-left font-medium text-slate-600 whitespace-nowrap">
                    {formatTime(r.t as number, bucket)}
                  </th>
                  {visible.map((s) => (
                    <td key={s.key} className="px-2.5 py-1 text-right text-slate-600">
                      {r[s.key] === null || r[s.key] === undefined ? "—" : fmt(r[s.key] as number)}
                    </td>
                  ))}
                  {medianVisible && (
                    <td className="px-2.5 py-1 text-right text-slate-600">
                      {r[MEDIAN_KEY] === null || r[MEDIAN_KEY] === undefined ? "—" : fmt(r[MEDIAN_KEY] as number)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- pieces ---------------------------------- */

/**
 * Legend chip: a color key + name + latest value, toggling one series.
 * Hidden chips read struck-through and muted; the swatch keeps the entity's hue
 * so the reader never has to re-learn a color.
 */
function LegendChip({ color, name, value, on, bold, dashed, onClick }: {
  color: string;
  name: string;
  value: string | null;
  on: boolean;
  bold?: boolean;
  dashed?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      title={on ? `Hide ${name}` : `Show ${name}`}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors cursor-pointer",
        on ? "border-slate-200 bg-white hover:border-brand-400" : "border-dashed border-slate-200 bg-slate-50 hover:border-brand-300",
      )}
    >
      {dashed ? (
        <svg width="14" height="4" aria-hidden="true" className="shrink-0" style={{ opacity: on ? 1 : 0.35 }}>
          <line x1="0" y1="2" x2="14" y2="2" stroke={color} strokeWidth={1.5} strokeDasharray="4 3" />
        </svg>
      ) : (
        <span
          className="w-2.5 h-2.5 rounded-full shrink-0"
          style={{ backgroundColor: color, opacity: on ? 1 : 0.35 }}
          aria-hidden="true"
        />
      )}
      <span className={cn(
        "font-medium",
        !on && "line-through text-slate-400",
        on && (bold ? "text-brand-950 font-semibold" : "text-slate-600"),
      )}>
        {name}
      </span>
      {value !== null && (
        <span className={cn("tabular-nums", on ? "text-slate-400" : "text-slate-300")}>{value}</span>
      )}
    </button>
  );
}

/**
 * Crosshair tooltip: the date header plus every visible series at that bucket —
 * a short line key in the series color, the name in secondary ink (text never
 * wears the data color) and the value leading in strong ink.
 */
function TrendTooltip({ active, label, payload, bucket, valueSuffix, seriesByKey, medianLabel }: {
  active?: boolean;
  label?: number;
  payload?: { dataKey?: string; value?: number | null }[];
  bucket: MetricBucket;
  valueSuffix: string;
  seriesByKey: Map<string, SeriesMeta>;
  medianLabel?: string;
}) {
  if (!active || !payload || payload.length === 0 || label === undefined) return null;

  // The hero area shares the primary line's dataKey — one row per series only.
  const seen = new Set<string>();
  const rows = payload
    .map((p) => {
      const key = String(p.dataKey ?? "");
      if (p.value === null || p.value === undefined || seen.has(key)) return null;
      seen.add(key);
      if (key === MEDIAN_KEY) return { key, name: medianLabel ?? "Median", color: MEDIAN_COLOR, value: p.value, dashed: true };
      const meta = seriesByKey.get(key);
      if (!meta) return null;
      return { key, name: meta.name, color: meta.color, value: p.value, dashed: false };
    })
    .filter((r): r is { key: string; name: string; color: string; value: number; dashed: boolean } => r !== null)
    .sort((a, b) => b.value - a.value);

  if (rows.length === 0) return null;

  return (
    <div className="pointer-events-none rounded-md border border-slate-200 bg-white px-2.5 py-1.5 shadow-md">
      <div className="mb-1 text-[10.5px] font-semibold text-slate-500 tabular-nums">{formatTime(label, bucket)}</div>
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-3 whitespace-nowrap text-[11px] leading-[1.5]">
          <svg width="12" height="4" aria-hidden="true" className="shrink-0">
            <line x1="0" y1="2" x2="12" y2="2" stroke={r.color} strokeWidth={2} strokeDasharray={r.dashed ? "3 2" : undefined} />
          </svg>
          <span className="flex-1 text-slate-500">{r.name}</span>
          <span className="font-semibold tabular-nums text-brand-950">{r.value.toFixed(1)}{valueSuffix}</span>
        </div>
      ))}
    </div>
  );
}
